/**
 * Business time, as the server sees it.
 *
 * The backend and MySQL run in the business timezone (e.g. Africa/Douala) and
 * decide dating, scopes and expiry with that clock. The browser's timezone or
 * clock can differ (travelling laptop, wrong PC settings), so anything that
 * means "today" or turns a YYYY-MM-DD string into a Date goes through here.
 *
 * Until /server/clock has answered, the browser clock and timezone are used.
 */

export type ServerClock = {
	timezone: string | null;
	tz_abbrev?: string;
	utc_offset_minutes: number;
	now_utc: string;
	today: string;
};

type ClockState = {
	timeZone: string | null;
	offsetMinutes: number | null;
	/** server epoch ms - browser epoch ms, measured at the last sync */
	skewMs: number;
	loaded: boolean;
};

const state: ClockState = { timeZone: null, offsetMinutes: null, skewMs: 0, loaded: false };

const listeners = new Set<() => void>();

/** Called after every successful sync (e.g. to redo "today" presets computed from the browser clock). */
export const subscribeBusinessClock = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

const pad = (n: number) => String(n).padStart(2, "0");

const isValidTimeZone = (timeZone: string): boolean => {
	try {
		new Intl.DateTimeFormat("en-CA", { timeZone });
		return true;
	} catch {
		return false;
	}
};

/** Apply a /server/clock payload. `requestStartedAt` lets network time be halved out of the skew. */
export const applyServerClock = (clock: ServerClock, requestStartedAt = Date.now(), receivedAt = Date.now()) => {
	const serverMs = Date.parse(clock.now_utc);
	if (Number.isFinite(serverMs)) {
		const midpoint = requestStartedAt + (receivedAt - requestStartedAt) / 2;
		state.skewMs = serverMs - midpoint;
	}
	state.timeZone = clock.timezone && isValidTimeZone(clock.timezone) ? clock.timezone : null;
	state.offsetMinutes = Number.isFinite(clock.utc_offset_minutes) ? clock.utc_offset_minutes : null;
	state.loaded = true;
	listeners.forEach((listener) => {
		try {
			listener();
		} catch {
			// A failing subscriber must not break the others.
		}
	});
};

export const resetBusinessClock = () => {
	state.timeZone = null;
	state.offsetMinutes = null;
	state.skewMs = 0;
	state.loaded = false;
};

export const isBusinessClockLoaded = () => state.loaded;

/** Current instant, corrected by the measured server skew. */
export const businessNowMs = (): number => Date.now() + state.skewMs;

const localDayString = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Business calendar day of an instant, as YYYY-MM-DD. */
export const businessDayOf = (epochMs: number): string => {
	if (state.timeZone) {
		// en-CA formats as YYYY-MM-DD.
		return new Intl.DateTimeFormat("en-CA", {
			timeZone: state.timeZone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		}).format(new Date(epochMs));
	}
	if (state.offsetMinutes !== null) {
		return new Date(epochMs + state.offsetMinutes * 60_000).toISOString().slice(0, 10);
	}
	return localDayString(new Date(epochMs));
};

/** Today in business time, as YYYY-MM-DD. */
export const businessToday = (): string => businessDayOf(businessNowMs());

/**
 * A YYYY-MM-DD day as a local Date at midnight, for date pickers and date-fns.
 * `new Date("2026-09-17")` would be UTC midnight and show the 16th west of UTC.
 */
export const parseISODay = (value: string | null | undefined): Date | null => {
	if (!value) return null;
	const match = ISO_DAY.exec(String(value).slice(0, 10));
	if (!match) {
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}
	return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

/** Today in business time as a local-midnight Date (picker default). */
export const businessTodayDate = (): Date => parseISODay(businessToday()) as Date;

/** Local calendar fields of a picked Date as YYYY-MM-DD (no timezone conversion). */
export const formatISODay = (value: Date): string => localDayString(value);

/** Compare YYYY-MM-DD strings against business today: -1 past, 0 today, 1 future. */
export const compareToBusinessToday = (day: string): -1 | 0 | 1 => {
	const today = businessToday();
	const value = String(day).slice(0, 10);
	return value < today ? -1 : value > today ? 1 : 0;
};

let syncing: Promise<void> | null = null;

/** Fetch /server/clock once (deduplicated); later calls refresh it. Failures keep the browser clock. */
export const syncBusinessClock = (baseUrl: string): Promise<void> => {
	if (syncing) return syncing;
	const startedAt = Date.now();
	syncing = fetch(`${baseUrl.replace(/\/?$/, "/")}server/clock`, { cache: "no-store" })
		.then((res) => (res.ok ? res.json() : null))
		.then((clock: ServerClock | null) => {
			if (clock) applyServerClock(clock, startedAt, Date.now());
		})
		.catch(() => undefined)
		.finally(() => {
			syncing = null;
		});
	return syncing;
};
